import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { formatDateWithDayOfTheWeek, formatTime, unique } from '../utils/util';
import { GQLCouponCodeFragment, GQLTestDatesQuery } from '../generated/graphql';
import { TestAreaPrefectureMap, testAreaLabelMap } from '../constants/test-area';
import { JapanMap } from '../map';
import dayjs, { Dayjs } from 'dayjs';
import { TestPlacesProps, NavigateProps, OLD_TEST_TYPE, AVAILABLE_RANGE } from '../entry-points/entry';
import { bindStateToRadio, valid } from '../utils/entry-utils';
import { RadioContainer, RadioLabel, Radio } from './entry-form-parts';
import { keyframes, styled } from 'styled-components'
import { isNotNullish } from '../../../api/src/utils/type-check'
import { ValueOf } from '../utils/type-utils'
import { prefectureLabelMap } from '../utils/enum-converter'

const Tag = styled.div`
padding: 0.35rem 0.5rem 0.25rem;
color: white;
font-size: 0.8rem;
line-height: 1;
`

const TagContainer = styled.div`
display: flex;
gap: 0.25rem;
align-items: center;
`

const TagLabel = styled.label`
	display: flex;
	gap: 0.25rem;
	align-items: center;
	font-size: 0.8rem;
	cursor: pointer;
	&:hover {
		opacity: 0.8;
	}
`

const TagLabelContainer = styled.div`
display: flex;
gap: 1rem;
align-items: center;
margin-bottom: 1rem;
flex-direction: column;
`

const TagCheckbox = styled.input`
	display: block;
	margin: 0;
	margin-right: 0.25rem;
`

const CheckboxContainer = styled.div`
	font-size: 0.8rem;
	display: flex;
	gap: 0.5rem;
	align-items: center;
	align-items: flex-end;
	margin-bottom: 1rem;
	@media (max-width: 40rem){
		flex-direction: column;
	}
`

const CheckboxLabel = styled.label`
	display: flex;
	gap: 0.25rem;
	align-items: center;
`

const Checkbox = styled.input`
	display: block;
	margin: 0;
`
const scroll = keyframes`
	from {
		transform: translateY(-0.5rem) rotate(135deg);
	}
	to {
		transform: translateY(0rem) rotate(135deg);
	}
`
const fade = keyframes`
	0%, 10%, 90%, 100% {
		opacity: 0;
	}
	25%, 75% {
		opacity: 1;
	}
`
const ScrollNav = styled.div`
	position: fixed;
	left: 0;
	bottom: 0;
	padding: 0.5rem 2rem;
	line-height: 1.5;
	font-size: 0.875rem;
	text-align: center;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: white;
	right: 0;
	margin-inline: auto;
	margin-bottom: 0.5rem;
	border-radius: 100rem;
	width: fit-content;
	gap: 2rem;
	transition: opacity 0.4s;
		z-index: 2;
		filter: drop-shadow(0 0.25rem 0.5rem #0004);
	&.hide {
		opacity: 0;
	}
	&::before, &::after {
		content: '';
		display: block;
		width: 0.75rem;
		height: 0.75rem;
		border: solid #000;
		border-width: 0.175rem 0.175rem 0 0;
		animation: ${scroll} 3s infinite ease-in-out, ${fade} 3s infinite ease-in-out;
	}
`

const PlaceButton = styled.a`
	border: 1px solid #ccc;
	border-radius: 0.25rem;
	padding: 0.25rem 0.5rem;
	display: block;
	text-decoration: none !important;
	font-size: 0.75rem;
	position: absolute;
	bottom: 0;
	right: 0;
`

const SearchContainer = styled.div`
	display: flex;
	align-items: center;
	margin-top: 1rem;
	margin-bottom: 1rem;
`

const SearchLabel = styled.div`
	font-size: 0.75rem;margin-right: 0.5em;
`

const SearchInput = styled.input`
	padding: 0.25rem;
	border: 1px solid #ccc;
	border-radius: 0.25rem;
	font-size: 1rem;
	flex: 1 1 auto;
`

const TestPlaceFilterCheckboxes = ({ a1, setA1, a2, setA2, reserch, setReserch }) => (
  <CheckboxContainer>
    会場を絞り込む:
    <CheckboxLabel>
      <Checkbox type="checkbox" checked={a1} onChange={e => setA1(e.currentTarget.checked)} />
      <div>がんリスク検査</div>
    </CheckboxLabel>
    <CheckboxLabel>
      <Checkbox type="checkbox" checked={a2} onChange={e => setA2(e.currentTarget.checked)} />
      <div>認知リスク検査</div>
    </CheckboxLabel>
    <CheckboxLabel>
      <Checkbox type="checkbox" checked={reserch} onChange={e => setReserch(e.currentTarget.checked)} />
      <div>研究参加コース</div>
    </CheckboxLabel>
  </CheckboxContainer>
)

export function SelectTestArea({ onNext, onPrev, testAreaState, testPlaces, testLocationState, testDateState, testDates, version, testType, couponCode }: TestPlacesProps & NavigateProps & { testAreaState: any; testLocationState: any; testDateState: any; testDates: GQLTestDatesQuery['testDates'] | undefined; version: number, testType: ValueOf<typeof OLD_TEST_TYPE> | null, couponCode: GQLCouponCodeFragment | null}) {
	const testPlace = testPlaces.find(p => p.id === testLocationState[0]);
	const [testDate, setTestDate] = useState<Dayjs | null>(null);

	const filteredTestDates = (testDates || []).filter(td => {
		const range = isNotNullish(testType) && AVAILABLE_RANGE[testType]
		const [from, to] = range || [null, null]
		return (!from || from.isBefore(td.startDate)) && (!to || to.isAfter(td.startDate)) && (testType !== OLD_TEST_TYPE.COUPON || !couponCode?.expirationDate || dayjs(td.startDate).isBefore(dayjs(couponCode?.expirationDate).endOf('date')))
	})
	const testDateList = unique(
		filteredTestDates.map(testDate => dayjs(testDate.startDate).startOf('day').valueOf()).sort((a, b) => a - b) ?? []).map(t => dayjs(t)
	);
	const isSingle = testPlaces.length < 2;

	const [a1, setA1] = useState(false)
	const [a2, setA2] = useState(false)

	const [keyword, setKeyword] = useState('')
	const [reserch, setReserch] = useState(false)

	useEffect(() => {
		if (isSingle) {
			const testPlace = testPlaces[0];
			if (testPlace) {
				testAreaState[1](Array.from(TestAreaPrefectureMap.entries()).find(([area, prefectures]) => prefectures.includes(testPlace.prefecture))?.[0]);
				setTimeout(() => {
					testLocationState[1](testPlace.id);
				}, 1);
			}
		}
	}, []);
	useEffect(() => {
		testLocationState[1](null);
		setTestDate(null);
		testDateState[1](null);
	}, [testAreaState[0], a1, a2]);
	useEffect(() => {
		setTestDate(null);
		testDateState[1](null);
	}, [testLocationState[0]]);
	useEffect(() => {
		testDateState[1](null);
	}, [testDate?.valueOf()]);

	const filteredTestPlaces = testPlaces
		.filter(p => (keyword.length > 0 || a1 || a2 || reserch) || TestAreaPrefectureMap.get(testAreaState[0])?.includes(p.prefecture))
		.filter(p => {
			if ([a1, a2, reserch].every(v => !v)) return true
			const checks = [
				a1 ? p.acceptA1 : true,
				a2 ? p.acceptA2 : true,
				reserch ? p.acceptStandardMb : true
			]
			return checks.every(Boolean)
		})
		.filter(p => {
			if(keyword.trim().length < 1) return true
			const keywords = keyword.split('\s').filter(v => v.length > 0)
			return keywords.every(keyword => {
				return [
					p.name,
					p.clinicName,
					p.address,
					p.access,
					p.detail
				].some(v => v.includes(keyword))
			})
		})

	useEffect(() => {
		const checkNavVisibility = () => {
			const placeHead = document.getElementById('test-place-head')
			const placeNav = document.getElementById('test-place-nav')
			const dateHead = document.getElementById('test-date-head')
			const dateNav = document.getElementById('test-date-nav')
			const timeHead = document.getElementById('test-time-head')
			const timeNav = document.getElementById('test-time-nav')
			if (placeHead && placeNav) {
				const rect = placeHead.getBoundingClientRect()
				if (rect.top <= 0 || rect.bottom <= window.innerHeight || testAreaState[0] === null) {
					placeNav.classList.add('hide')
				} else {
					placeNav.classList.remove('hide')
				}
			} else {
				placeNav?.classList.add('hide')
			}
			if (dateHead && dateNav) {
				const rect = dateHead.getBoundingClientRect()
				if (rect.top <= 0 || rect.bottom <= window.innerHeight || testLocationState[0] === null) {
					dateNav.classList.add('hide')
				} else {
					dateNav.classList.remove('hide')
				}
			} else {
				dateNav?.classList.add('hide')
			}
			if (timeHead && timeNav) {
				const rect = timeHead.getBoundingClientRect()
				if (rect.top <= 0 || rect.bottom <= window.innerHeight || testLocationState[0] === null) {
					timeNav.classList.add('hide')
				} else {
					timeNav.classList.remove('hide')
				}
			} else {
				timeNav?.classList.add('hide')
			}
		}
		window.addEventListener('scroll', checkNavVisibility)
		window.addEventListener('resize', checkNavVisibility)
		checkNavVisibility()
		return () => {
			window.removeEventListener('scroll', checkNavVisibility)
			window.removeEventListener('resize', checkNavVisibility)
		}
	}, [testAreaState[0], testLocationState[0]])

	return <>
		<ScrollNav id="test-place-nav">
			スクロールして<br />
			検査会場を選択
		</ScrollNav>
		<ScrollNav id="test-date-nav">
			スクロールして<br />
			検査日を選択
		</ScrollNav>
		<ScrollNav id="test-time-nav">
			スクロールして<br />
			検査時間を選択
		</ScrollNav>
		<h3>検査会場</h3>
		<p className="entry-form-text">検査を受けたいエリアを選択してください。</p>
		<SearchContainer>
			<SearchLabel>
				検索:
			</SearchLabel>
			<SearchInput value={keyword} onChange={e => setKeyword(e.currentTarget.value)} placeholder="住所・クリニック名など"/>
		</SearchContainer>
		<TestPlaceFilterCheckboxes a1={a1} setA1={setA1} a2={a2} setA2={setA2} reserch={reserch} setReserch={setReserch} />
		{!isSingle && ((!keyword && !a1 && !a2 && !reserch) || isNotNullish(testAreaState[0])) && <div id="select-area">
			<JapanMap onAreaClick={testAreaState[1]} activeTestArea={testAreaState[0]} disabledArea={Array.from(TestAreaPrefectureMap).filter(([testArea, prefectures]) => testPlaces.filter(testPlace => prefectures.includes(testPlace.prefecture)).length < 1).map(([testArea]) => testArea)} />
			<RadioContainer>
				{Array.from(testAreaLabelMap.entries()).map(([testArea, label]) => {
					const hasTestPlace = testPlaces.filter(p => TestAreaPrefectureMap.get(testArea)?.includes(p.prefecture)).length > 0
					return <RadioLabel
						key={testArea}
						state={testAreaState}
						value={testArea}
						disabled={!hasTestPlace}
					>
						<input type="radio" {...bindStateToRadio(testAreaState, testArea)} name="test-area" disabled={!hasTestPlace} />
						{label}
					</RadioLabel>
				})}
			</RadioContainer>
		</div>}
		{(testAreaState[0] !== null || keyword || a1 || a2 || reserch) && <>
			<h3 style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}} id="test-place-head">
				検査会場の選択
				<TagLabelContainer>
					<TagLabel>
						<TagCheckbox 
							type="checkbox" 
							checked={a1} 
							onChange={e => setA1(e.currentTarget.checked)} 
						/>
						<Tag style={{backgroundColor: '#6A48AD'}}>がん</Tag>
						がんリスク検査対応
					</TagLabel>
					{version > 0 && <TagLabel>
						<TagCheckbox 
							type="checkbox" 
							checked={a2} 
							onChange={e => setA2(e.currentTarget.checked)} 
						/>
						<Tag style={{backgroundColor: '#0094EA'}}>認知</Tag>
						認知リスク検査対応
					</TagLabel>}
					<TagLabel>
						<TagCheckbox 
							type="checkbox" 
							checked={reserch} 
							onChange={e => setReserch(e.currentTarget.checked)} 
						/>
						<Tag style={{backgroundColor: '#0b9954'}}>研究</Tag>
						研究参加コース対応
					</TagLabel>
				</TagLabelContainer>
			</h3>
			<SearchContainer>
				<SearchLabel>
					検索:
				</SearchLabel>
				<SearchInput value={keyword} onChange={e => setKeyword(e.currentTarget.value)} placeholder="住所・クリニック名など"/>
			</SearchContainer>
			{
				filteredTestPlaces.length > 0 ? filteredTestPlaces.map(testPlace => {
					return <Radio key={testPlace.id} state={testLocationState} value={testPlace.id} disabled={testPlace.disabled} name="test-location">
						<div className={classNames('location-radio-container', { disabled: testPlace.disabled })}>
							<p className="location-name">
								{testPlace.name}
									<TagContainer>
									{
										testPlace.acceptA1 && <Tag style={{backgroundColor: '#6A48AD'}}>がん</Tag>
									}
									{
										version > 0 && testPlace.acceptA2 && <Tag style={{backgroundColor: '#0094EA'}}>認知</Tag>
									}
									{
										testPlace.acceptStandardMb && <Tag style={{backgroundColor: '#0b9954'}}>研究</Tag>
									}
								</TagContainer>
							</p>
							<p className="location-clinic-name">{testPlace.clinicName}</p>
							<p className="location-access">{testPlace.access}</p>
							<p className="location-schedule">{testPlace.schedule}</p>
							{testPlace.note && <p className="location-schedule">{testPlace.note}</p>}
							<PlaceButton href={testPlace.mapUrl} target="_blank">
								地図を開く
							</PlaceButton>
						</div>
					</Radio>;
				}) : '選択可能な検査会場がありません。'
			}

		</>}
		{testLocationState[0] !== null && testPlace && <>
			<h3 id="test-date-head">検査日の選択</h3>
			{filteredTestDates ? <RadioContainer label="">
				{testDateList.map(d => {
					const dates = filteredTestDates.filter(td => dayjs(td.startDate).startOf('day').isSame(d)) ?? [];
					const disabled = dates.every(td => td.entryCount + td.otherPlatformEntryCount >= td.capacity || td.stopped);
					return <Radio<Dayjs | null>
						key={d.valueOf()}
						state={[testDate, setTestDate, () => true]}
						value={d}
						name="select-date"
						comparator={(a, b) => a?.valueOf() === b?.valueOf()}
						disabled={disabled}
					>
						<span style={{ textDecoration: disabled ? 'line-through' : '' }}>{formatDateWithDayOfTheWeek(d.toDate())}</span>
						{disabled && `【${dates.map(td => td.stoppedMessage).find(msg => msg) || '締め切りました'}】`}
					</Radio>;
				})}
			</RadioContainer> :
				'読み込み中…'}
		</>}
		{
			filteredTestDates && filteredTestDates.length < 1 && testPlace && '選択できる日程がありません。'
		}

		{testLocationState[0] !== null && testPlace && testDate && <>
			<h3 id="test-time-head">検査時間の選択</h3>
			<RadioContainer label="">
				{
					filteredTestDates?.filter(td => testDate?.isSame(dayjs(td.startDate), 'date')).map(td => {
						const disabled = td.entryCount + td.otherPlatformEntryCount >= td.capacity || td.stopped;
						return <Radio
							key={td.id}
							state={testDateState}
							value={td.id}
							name="test-date"
							disabled={disabled}
						>
							<span style={{ textDecoration: disabled ? 'line-through' : '' }}>{formatTime(td.startDate)} - {formatTime(td.endDate)}</span>
							{disabled ?
								`【${td.stoppedMessage || '定員に達しました'}】` :
								` 予約枠残り: ${Math.max(td.capacity - td.entryCount - td.otherPlatformEntryCount, 0)}名`}
						</Radio>;
					})
				}
			</RadioContainer>
		</>}

		{testPlaces.length < 1 && <p className="entry-form-text">現在選択できる検査会場がありません</p>}
		<div className="button-container">
			<button className="prev-button" onClick={() => {
				onPrev();
			}}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" disabled={!valid(testDateState)} onClick={() => {
				onNext();
			}}>次へ</button>
		</div>
	</>;
}
